import { getCandidateDeclarations } from "./root";
import { createLookupCombiner, createLookupSelectors } from "./lib";
import {
  selectCandidateDeclarationIdByAttachmentIdLookup,
  selectCandidateDeclarationIdByEnvdIdLookup,
  selectConsignmentIdsByNvd,
  selectConsignmentIdsByVendorPropertyIdLookup,
} from "./indexes";
import { selectDeploymentIdByConsignmentIdLookup } from "./consignmentsCalculated";
import { selectAgencyByDeploymentIdLookup } from "./agencies";

export const getCandidateDeclarationById =
  (id: string) =>
  (state: State): CandidateDeclaration | null =>
    getCandidateDeclarations(state)[id] || null;

export const getCandidateDeclarationIdByEnvdId =
  (eNvdId: string) =>
  (state: State): string | null =>
    selectCandidateDeclarationIdByEnvdIdLookup(state)[eNvdId] || null;

export const getCandidateDeclarationIdByAttachmentId =
  (attachmentId: string) =>
  (state: State): string | null =>
    selectCandidateDeclarationIdByAttachmentIdLookup(state)[attachmentId] ||
    null;

export interface SuggestedConsignmentMatch {
  consignmentId: string;
  score: number;
  matchedOn: { vendorPropertyId: boolean; nvd: boolean };
  agencyName: string | null;
  caveats: {
    wrongDeployment: boolean;
  };
}

export const [
  selectSuggestedConsignmentsByCandidateDeclarationIdLookup,
  getSuggestedConsignmentsByCandidateDeclarationId,
] = createLookupSelectors(
  [
    getCandidateDeclarations,
    selectConsignmentIdsByVendorPropertyIdLookup,
    selectConsignmentIdsByNvd,
    selectDeploymentIdByConsignmentIdLookup,
    selectAgencyByDeploymentIdLookup,
  ],
  createLookupCombiner(
    (
      candidateDeclaration: CandidateDeclaration,
      consignmentIdsByVendorPropertyIdLookup,
      consignmentIdsByNvd,
      deploymentIdByConsignmentIdLookup,
      agencyByDeploymentIdLookup,
    ): SuggestedConsignmentMatch[] => {
      const matchedOnByConsignmentId: Record<
        string,
        { vendorPropertyId: boolean; nvd: boolean }
      > = {};

      if (candidateDeclaration.originPropertyId) {
        const vendorPropertyConsignmentIds =
          consignmentIdsByVendorPropertyIdLookup[
            candidateDeclaration.originPropertyId
          ] || [];
        vendorPropertyConsignmentIds.forEach(consignmentId => {
          matchedOnByConsignmentId[consignmentId] = matchedOnByConsignmentId[
            consignmentId
          ] || { vendorPropertyId: false, nvd: false };
          matchedOnByConsignmentId[consignmentId].vendorPropertyId = true;
        });
      }
      if (candidateDeclaration.serialNumber) {
        const nvdConsignmentIds =
          consignmentIdsByNvd[candidateDeclaration.serialNumber] || [];
        nvdConsignmentIds.forEach(consignmentId => {
          matchedOnByConsignmentId[consignmentId] = matchedOnByConsignmentId[
            consignmentId
          ] || { vendorPropertyId: false, nvd: false };
          matchedOnByConsignmentId[consignmentId].nvd = true;
        });
      }

      const matches = Object.entries(matchedOnByConsignmentId).map(
        ([consignmentId, matchedOn]) => {
          const score =
            (matchedOn.vendorPropertyId ? 1 : 0) + (matchedOn.nvd ? 1 : 0);

          const consignmentDeploymentId =
            deploymentIdByConsignmentIdLookup[consignmentId];
          const agency = agencyByDeploymentIdLookup[consignmentDeploymentId];

          return {
            consignmentId,
            score,
            matchedOn,
            agencyName: agency?.shortName || agency?.name || null,
            caveats: {
              wrongDeployment:
                consignmentDeploymentId != null &&
                consignmentDeploymentId !== candidateDeclaration.deploymentId,
            },
          };
        },
      );

      matches.sort((a, b) => b.score - a.score);
      return matches;
    },
  ),
  [],
);
